import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Cookie, Settings, BarChart, Target, Shield, Info } from 'lucide-react';
import SEOHead from './SEOHead';
import { getBreadcrumbStructuredData } from '../utils/structuredData';
import Header from './Header';
import Footer from './Footer';

const CookiePolicy = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx/' },
    { name: 'Cookie Policy', url: 'https://laimu.mx/cookies' }
  ]);

  const seoTitle = language === 'en' 
    ? 'Cookie Policy - LAIMU Trantor | Website Cookies & Tracking'
    : 'Política de Cookies - LAIMU Trantor | Cookies del Sitio Web y Seguimiento';
    
  const seoDescription = language === 'en'
    ? 'Learn about LAIMU Trantor\'s cookie usage, tracking technologies, and how we use cookies to improve your browsing experience. Manage your cookie preferences and understand your privacy options.'
    : 'Conoce el uso de cookies de LAIMU Trantor, tecnologías de seguimiento y cómo usamos cookies para mejorar tu experiencia de navegación. Gestiona tus preferencias de cookies y comprende tus opciones de privacidad.';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url="/cookies"
        structuredData={breadcrumbData}
        keywords={language === 'en' 
          ? 'cookie policy, website cookies, tracking cookies, privacy settings, cookie management, LAIMU cookies, web analytics, user tracking'
          : 'política cookies, cookies sitio web, cookies seguimiento, configuración privacidad, gestión cookies, cookies LAIMU, analítica web, seguimiento usuario'
        }
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <Cookie className="w-6 h-6" style={{ color: '#115d7b' }} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{t.legal.cookies.title}</h1>
                  <p className="text-gray-600">{t.legal.cookies.lastUpdated}: {t.legal.cookies.date}</p>
                </div>
              </div>

              <div className="prose max-w-none">
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Info className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.cookies.what.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.cookies.what.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Settings className="w-6 h-6 mr-2" style={{ color: '#115d7b' }} />
                    {t.legal.cookies.types.title}
                  </h2>
                  <p className="text-gray-700 mb-4">{t.legal.cookies.types.intro}</p>
                  
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-green-800 mb-2 flex items-center">
                        <Shield className="w-5 h-5 mr-2" />
                        {t.legal.cookies.types.essential.title}
                      </h3>
                      <p className="text-green-700">{t.legal.cookies.types.essential.description}</p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2 flex items-center">
                        <BarChart className="w-5 h-5 mr-2" />
                        {t.legal.cookies.types.analytics.title}
                      </h3>
                      <p className="text-blue-700">{t.legal.cookies.types.analytics.description}</p>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-purple-800 mb-2 flex items-center">
                        <Settings className="w-5 h-5 mr-2" />
                        {t.legal.cookies.types.functional.title}
                      </h3>
                      <p className="text-purple-700">{t.legal.cookies.types.functional.description}</p>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-orange-800 mb-2 flex items-center">
                        <Target className="w-5 h-5 mr-2" />
                        {t.legal.cookies.types.marketing.title}
                      </h3>
                      <p className="text-orange-700">{t.legal.cookies.types.marketing.description}</p>
                    </div>
                  </div>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.cookies.thirdParty.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.cookies.thirdParty.intro}</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                    {t.legal.cookies.thirdParty.services.map((service, index) => (
                      <li key={index}>{service}</li>
                    ))}
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.cookies.control.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.cookies.control.intro}</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                    {t.legal.cookies.control.methods.map((method, index) => (
                      <li key={index}>{method}</li>
                    ))}
                  </ul>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">
                      <strong>{t.legal.cookies.control.warning.title}:</strong> {t.legal.cookies.control.warning.content}
                    </p>
                  </div>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.cookies.updates.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.cookies.updates.content}</p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.legal.cookies.contact.title}</h2>
                  <p className="text-gray-700 mb-4">{t.legal.cookies.contact.content}</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">
                      <strong>Email:</strong> privacy@laimu.mx<br />
                      <strong>{t.contact.phone}:</strong> +52 81 1107 1450<br />
                      <strong>{t.contact.address.line1}</strong><br />
                      {t.contact.address.line2}<br />
                      {t.contact.address.city}, {t.contact.address.state} {t.contact.address.zip}
                    </p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CookiePolicy;